import { useContext, useState } from 'react'
import { Calendar, ChevronDown, ChevronUp } from '@/assets/icons'
import { AuthContext } from '@/context/auth-context'
import clsx from 'clsx'
import { useAppSelector } from '@/store/store-hooks'
import { isPaxtonBrand } from '@/util/enterprise'
import SubscriptionNoticeBannerActions from './SubscriptionNoticeBannerActions'
import { selectActiveTrialEndingMessage, selectInTrialCanceledMessage } from '../../store/selectors'
import { StripeSubscriptionStatus, StripeSubscriptionInterval } from '../../store/schemas'
import { kSessionStorageSubscriptionNoticeBannerDismissedKey } from '../../constants'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'

/**
 * The variant of the subscription notice banner.
 * These are also used to track the variant in analytics and should not be changed.
 *
 * ACTIVE_TRIAL: The user is in a trial and the trial is ending soon.
 * IN_TRIAL_CANCELED: The user is in a trial and turns off auto-renewal.
 */
export enum SubscriptionNoticeBannerVariant {
  ACTIVE_TRIAL = 'ACTIVE_TRIAL',
  IN_TRIAL_CANCELED = 'IN_TRIAL_CANCELED',
}

/**
 * Subscription Notice Banner
 *
 * Displays a contextual in-app banner informing the user about their subscription status
 * and upcoming changes (e.g. trial ending, cancellation, upcoming payment).
 */
export default function SubscriptionNoticeBanner() {
  const { user, userAccountData } = useContext(AuthContext)

  // Local state
  const [expanded, setExpanded] = useState<boolean>(false)
  const [dismissed, setDismissed] = useState<boolean>(() => sessionStorage.getItem(kSessionStorageSubscriptionNoticeBannerDismissedKey) === 'true')

  // User data
  const isAnonUser = !user || user.isAnonymous
  const isOrgMemberWithoutPayerRole = userAccountData?.isOrgAccount && !userAccountData.isOrgPayer

  // Redux state
  const accountDataInitialLoadComplete = useAppSelector((state) => state.accountState.initialLoadComplete)
  const stripeSubscriptionStatus = useAppSelector((state) => state.accountState.stripeSubscriptionStatus)
  const stripeSubscriptionInterval = useAppSelector((state) => state.accountState.stripeSubscriptionInterval)
  const cancelAtPeriodEnd = useAppSelector((state) => state.accountState.cancelAtPeriodEnd)

  const activeTrialEndingMessage = useAppSelector((state) => selectActiveTrialEndingMessage(state))
  const inTrialCanceledMessage = useAppSelector((state) => selectInTrialCanceledMessage(state))

  // Inferred values
  const isInTrial = stripeSubscriptionStatus === StripeSubscriptionStatus.TRIALING
  const variant = cancelAtPeriodEnd ? SubscriptionNoticeBannerVariant.IN_TRIAL_CANCELED : SubscriptionNoticeBannerVariant.ACTIVE_TRIAL
  const variantTextColor = variant === SubscriptionNoticeBannerVariant.IN_TRIAL_CANCELED ? 'text-yellow-800' : 'text-brand-800'
  const infoMessage = variant === SubscriptionNoticeBannerVariant.ACTIVE_TRIAL ? activeTrialEndingMessage : inTrialCanceledMessage

  // Analytics tracking
  const { trackEvent } = useAnalytics()

  /**
   * EARLY EXIT - Don't show the banner if:
   * - user is not a Paxton user (is enterprise, e.g. haiku)
   * - feature flag is disabled
   * - user is anonymous
   * - user is in an org, but is not the payer of the org
   * - account data has not been loaded
   * - user is not in trial
   * - user has dismissed the banner
   */
  if (!isPaxtonBrand() || isAnonUser || isOrgMemberWithoutPayerRole || !accountDataInitialLoadComplete || !isInTrial || dismissed) {
    return null
  }

  /**
   * Handles temporary dismissal of the banner for the current session.
   * Uses sessionStorage so the banner reappears in a new browser session.
   */
  const handleDismiss = () => {
    sessionStorage.setItem(kSessionStorageSubscriptionNoticeBannerDismissedKey, 'true')
    setDismissed(true)
    trackEvent(AnalyticsEvent.SubscriptionNoticeBannerDismissed, {
      subscriptionNoticeBannerVariant: variant,
      subscriptionInterval: stripeSubscriptionInterval as StripeSubscriptionInterval,
    })
  }

  return (
    <div
      className={clsx(
        'w-full border-b px-4 py-3 flex flex-col items-center lg:flex-row lg:items-center lg:justify-between text-sm text-balance',
        variant === SubscriptionNoticeBannerVariant.IN_TRIAL_CANCELED && 'bg-yellow-50 border-yellow-200',
        variant === SubscriptionNoticeBannerVariant.ACTIVE_TRIAL && 'bg-brand-50 border-brand-200'
      )}
    >
      {/* Trial Ending Header - button on mobile (for expand/collapse), static on desktop */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls="subscription-banner-details"
        aria-labelledby="subscription-banner-toggle"
        className="flex items-center justify-center lg:justify-start w-full lg:w-auto space-x-1.5 lg:pointer-events-none lg:focus:outline-none whitespace-nowrap"
      >
        {/* Icon + Title */}
        <Calendar className={clsx('w-4 h-4 flex-shrink-0', variantTextColor)} aria-hidden="true" />
        <span className={clsx('font-semibold', variantTextColor)}>Free trial ending soon</span>

        {/* MOBILE: Chevron toggle (screens < lg) */}
        <span id="subscription-banner-toggle" className={clsx('lg:hidden', variantTextColor)} aria-hidden="true">
          {expanded ? <ChevronUp className="w-5 h-5" aria-hidden="true" /> : <ChevronDown className="w-5 h-5" aria-hidden="true" />}
        </span>

        {/* DESKTOP: Separator + info message (screens >= lg) */}
        <span className={clsx('hidden lg:inline mx-2', variantTextColor)}>|</span>
        <span className="hidden lg:inline">{infoMessage}</span>
      </button>

      {/* MOBILE: Expandable info + action buttons (screens < lg) */}
      <div
        id="subscription-banner-details"
        aria-hidden={!expanded}
        className={clsx('lg:hidden overflow-hidden transition-all duration-500 ease-in-out', expanded ? 'max-h-64' : 'max-h-0')}
      >
        <div className="mt-3 mx-2 text-center flex flex-col items-center space-y-2">
          <div>{infoMessage}</div>

          <div className="flex items-center">
            <SubscriptionNoticeBannerActions variant={variant} onDismiss={handleDismiss} />
          </div>
        </div>
      </div>

      {/* DESKTOP: Action buttons (screens >= lg) */}
      <div className="hidden lg:inline-block">
        <SubscriptionNoticeBannerActions variant={variant} onDismiss={handleDismiss} />
      </div>
    </div>
  )
}
